import clsx from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { html, literal } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './sidenav.scss?inline'

// Mapped rather than interpolated straight into the static template: `landmark` is a
// plain attribute, so any string can reach it from markup, and `unsafeStatic` would
// render that string as a tag name. Unknown values fall back to `aside`.
const LANDMARK_TAGS = {
  aside: literal`aside`,
  nav: literal`nav`,
} as const

export type SideNavLandmark = keyof typeof LANDMARK_TAGS

/**
 * `dap-ds-sidenav`
 * @summary Side navigation is a list of links that are used to navigate to different sections of a page.
 * @element dap-ds-sidenav
 * @title - Side navigation
 *
 * @event {{ href: string, event: Event }} dds-item-click - Event fired when the side navigation item is clicked.
 *
 * @slot - The content of the side navigation.
 *
 * @csspart base - The main side navigation container.
 * @csspart menu - The menu of the side navigation.
 */
export default class DapDSSideNav extends DdsElement {
  static tagName = 'dap-ds-sidenav'

  /** The label of the side navigation, if aria label is not provided, it will be used as aria label */
  @property({ reflect: true }) label: string

  /** The active href of the side navigation, to explicitly set the active item, if not provided, it will be set based on the current location */
  @property() activeHref: string

  /** The size of the side navigation */
  @property({ type: String }) size: 'xs' | 'sm' | 'lg' = 'xs'

  /**
   * The landmark element to render. `aside` exposes a complementary landmark; `nav`
   * exposes a navigation landmark, so the sidenav is listed when a screen-reader user
   * asks for the page's navigation.
   *
   * Defaults to `aside` because that is what this component has always rendered, and
   * changing the landmark of every existing sidenav is not this component's call: a
   * sidenav that is genuinely a sidebar should stay complementary. Set `nav` when the
   * sidenav is the primary way to move around the view.
   * @type {'aside' | 'nav'}
   */
  @property({ reflect: true, type: String }) landmark: SideNavLandmark = 'aside'

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    const activeItem = this.querySelector(
      `[href="${this.activeHref ? this.activeHref : window.location.pathname}"]`,
    )

    if (activeItem) {
      this.removeActive()
      this.setActive(activeItem)
    }

    const activeGroup = activeItem?.closest('dap-ds-sidenav-group')
    if (activeGroup) {
      activeGroup.setAttribute('open', 'true')
    }

    // go through all the items and and groups and set the size
    this.querySelectorAll('dap-ds-sidenav-item, dap-ds-sidenav-group').forEach(
      item => {
        ;(item as any).size = this.size
      },
    )
  }

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    super.attributeChangedCallback(name, _old, value)

    if (name === 'activehref') {
      this.removeActive()

      const activeItem = this.querySelector(`[href="${value}"]`)
      this.setActive(activeItem)
    }
  }

  private removeActive(): void {
    this.querySelectorAll('dap-ds-sidenav-item, dap-ds-sidenav-group').forEach(
      item => {
        item.removeAttribute('active')
      },
    )
  }

  private setActive(activeItem?: Element | null): void {
    if (activeItem) {
      activeItem.setAttribute('active', 'true')

      const activeGroup = activeItem.closest('dap-ds-sidenav-group')
      if (activeGroup) {
        activeGroup.setAttribute('open', 'true')
      }
    }
  }

  // Emits `dds-item-click` when an item (or a group acting as a link) is
  // activated. The event is cancelable: consumers doing client-side routing can
  // call `event.preventDefault()` to suppress the item's native anchor
  // navigation. When nobody prevents it, the anchor navigates as before.
  private handleClick(event: MouseEvent): void {
    const item = event
      .composedPath()
      .find(
        (el): el is HTMLElement =>
          el instanceof HTMLElement &&
          (el.tagName === 'DAP-DS-SIDENAV-ITEM' ||
            el.tagName === 'DAP-DS-SIDENAV-GROUP'),
      )

    // `href` is a non-reflecting reactive property, so a framework that sets it
    // as a DOM property (e.g. React 19 for custom elements) leaves no `href`
    // attribute. Read the property first, then fall back to the attribute.
    const href =
      (item as (HTMLElement & { href?: string }) | undefined)?.href ||
      item?.getAttribute('href')
    if (!href) return

    const itemClick = this.emit(
      'dds-item-click',
      { href, event },
      { cancelable: true },
    )

    if (itemClick.defaultPrevented) {
      event.preventDefault()
    }
  }

  render() {
    const tag = LANDMARK_TAGS[this.landmark] ?? LANDMARK_TAGS.aside

    return html`
      <${tag}
        part="base"
        class=${clsx('sidenav', `sidenav--${this.size}`)}
        aria-label=${this.ariaLabel ?? this.label}>
        <menu part="menu" @click=${this.handleClick}>
          <slot></slot>
        </menu>
      </${tag}>
    `
  }
}
